import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Navigate, NavLink, Route, Routes } from 'react-router-dom'
import { api } from './lib/api'
import { SEAL_APPROVERS } from './lib/memoTerms'

import Dashboard from './pages/Dashboard'
import Todos from './pages/Todos'
import Evaluations from './pages/Evaluations'
import WorkOrders from './pages/WorkOrders'
import WorkOrderForm from './pages/WorkOrderForm'
import Summary from './pages/Summary'
import Acceptances from './pages/Acceptances'
import AcceptanceForm from './pages/AcceptanceForm'
import AcceptanceEdit from './pages/AcceptanceEdit'
import Billings from './pages/Billings'
import Masters from './pages/Masters'
import SyncSettings from './pages/SyncSettings'
import Login from './pages/Login'
import Users from './pages/Users'
import Reviews from './pages/Reviews'
import Memos from './pages/Memos'
import MemoForm from './pages/MemoForm'
import GeneralSeal from './pages/seal/GeneralSeal'
import ContractSeal from './pages/seal/ContractSeal'
import WorkOrderPrint from './pages/print/WorkOrderPrint'
import WorkOrdersBatchPrint from './pages/print/WorkOrdersBatchPrint'
import AcceptancesBatchPrint from './pages/print/AcceptancesBatchPrint'
import AcceptancePrint from './pages/print/AcceptancePrint'
import MemoPrint from './pages/print/MemoPrint'

type AuthState = 'checking' | 'signed-in' | 'signed-out'

/** 選單次分組的收合狀態存這裡；{ 分組名: true } 代表收合 */
const NAV_COLLAPSED_KEY = 'winho.nav.collapsed'

function loadCollapsed(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(NAV_COLLAPSED_KEY) ?? '{}')
  } catch {
    return {}   // 存壞了就當全部展開，不要因為這個讓整個介面掛掉
  }
}

/**
 * 可收合的選單次分組
 *
 * 箭頭用 SVG 而不是文字符號（▸／▾）：文字符號在不同系統的字型下大小與基線都不一樣，
 * 選單上一排排看過去會歪。SVG 靠 CSS 旋轉，展開收合是同一個圖形轉 90 度。
 */
function SubGroup({
  title,
  open,
  onToggle,
  children,
}: {
  title: string
  open: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <>
      <button type="button" className="subgroup" aria-expanded={open} onClick={onToggle}>
        {title}
        <svg className={`caret${open ? ' open' : ''}`} viewBox="0 0 16 16" aria-hidden="true">
          <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && children}
    </>
  )
}

export default function App() {
  const [user, setUser] = useState<{ email: string; name: string; role: string } | null>(null)
  const [auth, setAuth] = useState<AuthState>('checking')
  // 側邊選單的待辦紅點。只要數字，不必把整包清單拉回來
  const [todoCount, setTodoCount] = useState(0)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(loadCollapsed)

  const check = useCallback(() => {
    setAuth('checking')
    api
      .get<{ user: { email: string; name: string; role: string } }>('/me')
      .then((d) => { setUser(d.user); setAuth('signed-in') })
      .catch(() => { setUser(null); setAuth('signed-out') })
  }, [])

  useEffect(check, [check])

  useEffect(() => {
    if (auth !== 'signed-in') return
    api
      .get<{ badge: number }>('/todos?count=1')
      .then((d) => setTodoCount(d.badge))
      .catch(() => setTodoCount(0)) // 紅點拿不到就不顯示，不必打斷整個介面
  }, [auth])

  /** 收合狀態記在瀏覽器，換頁或重整都保持原樣；預設全部展開 */
  const toggleGroup = (key: string) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))

  // 寫檔放 effect，不放在 setState 的 updater 裡 —— updater 可能被 React 重複呼叫
  useEffect(() => {
    try { localStorage.setItem(NAV_COLLAPSED_KEY, JSON.stringify(collapsed)) } catch { /* 無痕模式寫不進去就算了 */ }
  }, [collapsed])

  const signOut = async () => {
    try { await api.post('/auth/logout') } catch { /* 即使失敗也要回登入畫面 */ }
    setUser(null)
    setAuth('signed-out')
  }

  if (auth === 'checking') {
    return <div className="empty" style={{ paddingTop: 80 }}>載入中…</div>
  }
  if (auth === 'signed-out') {
    return <Login onSignedIn={check} />
  }

  const isAdmin = user?.role === 'admin'
  /* 合約專用章限名單內的人，其他管理員也看不到這個入口 */
  const canSeal = !!user && SEAL_APPROVERS.includes(user.email)

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <img src="/logo-mark.png" alt="" />
          <div>
            <h1>文案外包管理</h1>
            <p className="sub">崴浤科技</p>
          </div>
        </div>
        <nav>
          <div className="group">首頁</div>
          {isAdmin && <NavLink to="/" end>儀表板</NavLink>}
          <NavLink to="/todos" end>
            待辦事項
            {todoCount > 0 && <span className="nav-badge">{todoCount}</span>}
          </NavLink>
          <NavLink to="/todos/mine" className="sub">我的案件</NavLink>
          {isAdmin && <NavLink to="/reviews" className="sub">審核待處理</NavLink>}

          <div className="group">執行項目</div>

          <SubGroup title="發包申請" open={!collapsed['發包申請']} onToggle={() => toggleGroup('發包申請')}>
            <NavLink to="/evaluations" className="sub">每月申請評估</NavLink>
            <NavLink to="/work-orders" end className="sub">發包單列表</NavLink>
            <NavLink to="/work-orders/new" className="sub">新增發包單（補）</NavLink>
            <NavLink to="/summary" className="sub">發包申請匯總</NavLink>
          </SubGroup>

          <SubGroup title="驗收請款" open={!collapsed['驗收請款']} onToggle={() => toggleGroup('驗收請款')}>
            <NavLink to="/acceptances/new" className="sub">新增驗收單</NavLink>
            <NavLink to="/acceptances" end className="sub">驗收單列表</NavLink>
            <NavLink to="/billings" className="sub">請款明細（外）</NavLink>
            <NavLink to="/billings/internal" className="sub">請款明細（內）</NavLink>
          </SubGroup>

          <SubGroup title="合作備忘錄" open={!collapsed['合作備忘錄']} onToggle={() => toggleGroup('合作備忘錄')}>
            <NavLink to="/memos" end className="sub">備忘錄列表</NavLink>
            <NavLink to="/memos/new" className="sub">新增備忘錄</NavLink>
          </SubGroup>

          {/* 合約用印只給印章授權人看；其他人只有「一般用印」這個入口 */}
          <SubGroup title="電子簽章" open={!collapsed['電子簽章']} onToggle={() => toggleGroup('電子簽章')}>
            <NavLink to="/seal/general" className="sub">一般用印</NavLink>
            {canSeal && <NavLink to="/seal/contract" className="sub">合約用印</NavLink>}
          </SubGroup>

          <div className="group">設定</div>
          <NavLink to="/masters">主檔維護</NavLink>
          {isAdmin && <NavLink to="/sync">Sheet 同步設定</NavLink>}
          {isAdmin && <NavLink to="/users">使用者權限</NavLink>}
        </nav>
        <div className="who">
          {user ? user.email : '未登入'}
          <button
            onClick={signOut}
            style={{
              display: 'block', marginTop: 8, width: '100%', padding: '4px 8px',
              fontSize: 11, background: 'transparent', color: '#7e909f',
              borderColor: '#23384b',
            }}
          >
            登出
          </button>
        </div>
      </aside>

      <main className="main">
        <Routes>
          {/* 儀表板僅限管理員；一般使用者的首頁是待辦事項 */}
          <Route path="/" element={isAdmin ? <Dashboard /> : <Navigate to="/todos" replace />} />
          <Route path="/todos" element={<Todos />} />
          {/* 我的案件＝待辦事項只留自己負責的，共用同一個頁面 */}
          <Route path="/todos/mine" element={<Todos mine />} />
          {/* 審核限管理員；直接打網址也要擋 */}
          <Route path="/reviews" element={isAdmin ? <Reviews /> : <Navigate to="/todos" replace />} />
          <Route path="/evaluations" element={<Evaluations />} />
          <Route path="/work-orders" element={<WorkOrders />} />
          <Route path="/work-orders/new" element={<WorkOrderForm />} />
          <Route path="/work-orders/:id" element={<WorkOrderForm />} />
          <Route path="/work-orders/:id/print" element={<WorkOrderPrint />} />
          <Route path="/work-orders/print-batch" element={<WorkOrdersBatchPrint />} />
          <Route path="/summary" element={<Summary />} />
          <Route path="/acceptances" element={<Acceptances />} />
          <Route path="/acceptances/new" element={<AcceptanceForm />} />
          <Route path="/acceptances/print-batch" element={<AcceptancesBatchPrint />} />
          <Route path="/acceptances/:id/print" element={<AcceptancePrint />} />
          <Route path="/acceptances/:id/edit" element={<AcceptanceEdit />} />
          <Route path="/billings" element={<Billings />} />
          <Route path="/billings/internal" element={<Billings dispatch="內包" />} />
          <Route path="/memos" element={<Memos />} />
          <Route path="/memos/new" element={<MemoForm />} />
          <Route path="/memos/:id" element={<MemoForm />} />
          <Route path="/memos/:id/print" element={<MemoPrint />} />
          <Route path="/seal/general" element={<GeneralSeal />} />
          {/* 頁面內另外再驗一次授權名單，不能只靠選單藏起來 */}
          <Route path="/seal/contract" element={<ContractSeal />} />
          <Route path="/masters" element={<Masters />} />
          {/* 這兩頁限管理員；直接打網址也要擋，只藏選單等於沒擋 */}
          <Route path="/sync" element={isAdmin ? <SyncSettings /> : <Navigate to="/todos" replace />} />
          <Route path="/users" element={isAdmin ? <Users /> : <Navigate to="/todos" replace />} />
        </Routes>
      </main>
    </div>
  )
}
